type Task<T = any> = () => Promise<T>

interface QueueItem {
  key?: string
  priority?: boolean
  run: () => Promise<void>
}

class ThumbnailQueue {
  private queue: Array<QueueItem> = []

  private isProcessing = false

  private processTimer: any = null

  public enqueue<T>(task: Task<T>, key?: string, priority = false): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (key) {
        const existingIndex = this.queue.findIndex((item) => item.key === key)
        if (existingIndex !== -1) {
          this.queue.splice(existingIndex, 1)
        }
      }

      const item: QueueItem = {
        key,
        priority,
        run: async () => {
          try {
            const res = await task()
            resolve(res)
          } catch (err) {
            reject(err)
          }
        }
      }

      if (priority) {
        const lastPriorityIndex = this.queue
          .map((q) => q.priority)
          .lastIndexOf(true)
        if (lastPriorityIndex === -1) {
          this.queue.unshift(item)
        } else {
          this.queue.splice(lastPriorityIndex + 1, 0, item)
        }
      } else {
        this.queue.push(item)
      }

      this.scheduleProcess()
    })
  }

  private scheduleProcess(): void {
    if (this.isProcessing) {
      return
    }

    if (this.processTimer) {
      clearTimeout(this.processTimer)
    }

    this.processTimer = setTimeout(() => {
      this.processTimer = null
      this.processNext()
    }, 50)
  }

  private async processNext(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) {
      return
    }

    this.isProcessing = true
    const current = this.queue.shift()

    if (current) {
      try {
        await current.run()
      } catch (err) {
        // Handled by task promise
      } finally {
        await new Promise((resolve) => {
          setTimeout(resolve, 800)
        })
        this.isProcessing = false
        this.processNext()
      }
    } else {
      this.isProcessing = false
    }
  }

  public clear(): void {
    if (this.processTimer) {
      clearTimeout(this.processTimer)
      this.processTimer = null
    }
    this.queue = []
  }
}

export const thumbnailQueue = new ThumbnailQueue()
export default thumbnailQueue
